import { computed, defineAsyncComponent, markRaw, unref } from 'vue';
import { BRAND, USER } from '@/models/auth/permissions.enum';
import { useAuthStore } from '@/stores/auth';
import { ADD_TO_CAMPAIGN_DISABLED_TOOLTIP } from '@/app/library/constants';
import { camelizeKeys } from 'humps';
import { constants } from '@/config';
import { useAddToActionMenuStore } from '@/stores/add-to-action-menu';
import { isMediaEligibleForCampaign } from '@/app/campaigns/utils';

export function useAddToCampaignAction(
  selectedMedia,
  isCreatorMedia,
  addMediaToCampaignsCallback = () => null,
) {
  const authStore = useAuthStore();
  const addToActionMenuStore = useAddToActionMenuStore();

  const formattedSelectedMedia = computed(() =>
    unref(selectedMedia).map((media) => camelizeKeys(media)),
  );

  const hasAccessToCampaigns = computed(
    () =>
      authStore.guard(USER.CAMPAIGNS.CAN_ACCESS_CAMPAIGNS) &&
      authStore.guard(BRAND.CAMPAIGNS.CAN_ACCESS_CAMPAIGNS),
  );

  const optionDisabled = computed(() => {
    if (!hasAccessToCampaigns.value || isCreatorMedia) return true;
    return formattedSelectedMedia.value.some((m) => !isMediaEligibleForCampaign(m));
  });

  async function addSelectedMediaToSelections() {
    await addToActionMenuStore.addSelectedMediaToSelections(
      formattedSelectedMedia.value,
      addMediaToCampaignsCallback,
    );
  }

  const addToCampaignOption = computed(() => ({
    value: constants.CAMPAIGNS,
    label: 'Campaigns',
    icon: 'campaign',
    disabled: optionDisabled.value,
    tooltip: optionDisabled.value ? ADD_TO_CAMPAIGN_DISABLED_TOOLTIP : undefined,
    hide: !hasAccessToCampaigns.value,
    badge: addToActionMenuStore.selectedCampaigns.length || undefined,
    command: addSelectedMediaToSelections,
    form: markRaw(defineAsyncComponent(() => import('@/components/AddToCampaignsSelectList.vue'))),
  }));

  return {
    addToCampaignOption,
    addSelectedMediaToSelections,
  };
}
